import { useEffect, useRef, useState } from 'react';

import { HARDENING_UNKNOWN, type CyberNetworkRow } from '../model/cyber.js';
import type { Selection } from '../model/selection.js';

/**
 * The strip under the cyber canvas — Phase 6's band strip, at its second
 * instance and for the same reason.
 *
 * *A sentence that is right beats a mark that is wrong.* Four facts about this
 * picture have no channel on the canvas, and three of them are the phase's
 * must-nots:
 *
 * | Wire | Heading | What the row says |
 * | --- | --- | --- |
 * | `networks[].router = null` | `ROUTER UNKNOWN` | the enclave is reached, its way in is not |
 * | `networks[].hardening = null` | `UNASSESSED` | RB §5.6.3 — Reconnoiter is what buys it |
 * | `cyber.footholds[]` | `FOOTHOLD` | which enclaves this team is inside |
 * | `cyber.friendlyIsolated[]` | `ISOLATED` | which of our own machines we have cut off |
 *
 * The canvas draws a card for an unnamed router and a dashed lane for an
 * unassessed posture, and both are *marks*: they say "not this" without saying
 * what. This is where they say it.
 *
 * **What is deliberately not here is the host count and the access rung.** The
 * renderer's own segment header prints `FOOTHOLD · 03 HOSTS` a few pixels above
 * this row, in the same words — so repeating either would be two statements of
 * one fact a foot apart, and in the host count's case two *different numbers*,
 * because the header counts machines hanging off the router and an enclave also
 * carries its router and its docked teams. Both are in the selection rail, which
 * is the detail surface and is where a subject restates itself in full.
 *
 * **The enclave's posture is stated here and on the enclave only.** Every asset
 * that hangs off it carries its own posture on its own footing tick, which is a
 * different fact about a different machine — must-not #2 is about not confusing
 * the two, not about drawing neither.
 *
 * Rows are clickable and right-clickable, exactly as the band strip's are, so an
 * enclave is one gesture from the selection rail whether the operator found it
 * on the canvas or here.
 */

export const CYBER_STRIP_MIN_HEIGHT = 92;

export function NetworkStrip(props: {
  readonly rows: readonly CyberNetworkRow[];
  readonly selected: string | null;
  readonly onSelect: (selection: Selection) => void;
  readonly onContextMenu: (id: string, x: number, y: number) => void;
}): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Whether the row of enclave cards runs past the right edge — the native
  // scrollbar is the only cue otherwise, and this strip has no bottom margin for
  // one to sit in.
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (el === null) return;
    const check = (): void => {
      setOverflowing(el.scrollWidth - el.clientWidth - el.scrollLeft > 1);
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', check);
      observer.disconnect();
    };
  }, [props.rows.length]);

  return (
    <div style={{ position: 'relative', minHeight: CYBER_STRIP_MIN_HEIGHT }}>
      <div
        ref={scrollRef}
        data-testid="cyber-strip"
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 1,
          height: '100%',
          padding: '1px 0 0',
          background: 'var(--rule-faint)',
          borderTop: '1px solid var(--border-strong)',
          overflowX: 'auto',
        }}
      >
        {props.rows.length === 0 ? (
          <div
            data-testid="cyber-strip-empty"
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              padding: '0 14px',
              background: 'var(--surface)',
              fontFamily: 'var(--font-data)',
              fontSize: 11,
              letterSpacing: '.12em',
              color: 'var(--text-muted)',
            }}
          >
            NO ENCLAVES IN THIS PICTURE
          </div>
        ) : (
          props.rows.map((row) => <Cell key={row.id} row={row} {...props} />)
        )}
      </div>
      {overflowing && (
        // Cut, not faded: a hairline-bordered tick, not a gradient — this system
        // has no soft edges. Pointer-events stay off so it never steals the click
        // meant for the card underneath it.
        <div
          data-testid="cyber-strip-more"
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 1,
            right: 0,
            bottom: 0,
            width: 13,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--surface-3)',
            borderLeft: '1px solid var(--border-strong)',
            pointerEvents: 'none',
          }}
        >
          <span
            style={{ fontFamily: 'var(--font-data)', fontSize: 10, color: 'var(--text-muted)' }}
          >
            {'›'}
          </span>
        </div>
      )}
    </div>
  );
}

function Cell(props: {
  readonly row: CyberNetworkRow;
  readonly selected: string | null;
  readonly onSelect: (selection: Selection) => void;
  readonly onContextMenu: (id: string, x: number, y: number) => void;
}): JSX.Element {
  const row = props.row;
  const on = props.selected === row.id;
  return (
    <div
      data-testid="cyber-strip-cell"
      data-network={row.id}
      data-side={row.net}
      role="button"
      tabIndex={0}
      onClick={() => props.onSelect({ kind: 'NETWORK', id: row.id })}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          props.onSelect({ kind: 'NETWORK', id: row.id });
        }
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
        props.onContextMenu(row.id, event.clientX, event.clientY);
      }}
      style={{
        flex: '1 1 0',
        minWidth: 190,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        padding: '8px 12px 10px',
        // The roster row's own idiom (`RosterRow.tsx`): side is ink on the id, not a
        // border, because the border is spoken for — selection is a 2px cyan edge
        // plus the cyan tint everywhere else this act happens (`RosterRow`,
        // `BandStrip`). A thick side-coloured border here was two different jobs
        // fighting over one edge.
        background: on ? 'var(--accent-tint)' : 'var(--surface)',
        borderLeft: '2px solid ' + (on ? 'var(--accent)' : 'transparent'),
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          style={{
            fontFamily: 'var(--font-data)',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '.1em',
            color: row.net === 'BLU' ? 'var(--blue)' : 'var(--red)',
          }}
        >
          {row.id}
        </span>
        <span
          data-testid="cyber-strip-side"
          style={{
            marginLeft: 'auto',
            fontFamily: 'var(--font-data)',
            fontSize: 10,
            letterSpacing: '.1em',
            color: row.net === 'BLU' ? 'var(--blue)' : 'var(--red)',
          }}
        >
          {row.net === 'BLU' ? 'FRIENDLY' : 'ADVERSARY'}
        </span>
      </div>

      {/* The router, and the whole of must-not #4's first half. An enclave whose
          way in the projection would not name says so in words — the canvas can
          only draw a card that is *not* labelled with a machine. */}
      <Line
        label="ROUTER"
        value={row.router}
        tone={row.routerNamed ? 'plain' : 'warn'}
        testid="cyber-strip-router"
      />

      {/* Posture, said once, on the network. */}
      <Line
        label="POSTURE"
        value={row.hardening}
        tone={row.hardening === HARDENING_UNKNOWN ? 'warn' : 'plain'}
        testid="cyber-strip-posture"
      />

      {row.foothold && (
        <Line label="FOOTHOLD" value="HELD" tone="plain" testid="cyber-strip-foothold" />
      )}
      {row.isolated.length > 0 && (
        <Line
          label="ISOLATED"
          value={row.isolated.join(' · ')}
          tone="warn"
          testid="cyber-strip-isolated"
        />
      )}
    </div>
  );
}

function Line(props: {
  readonly label: string;
  readonly value: string;
  readonly tone: 'plain' | 'warn' | 'bad';
  readonly testid: string;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
      <span
        style={{
          flex: 'none',
          width: 66,
          fontFamily: 'var(--font-data)',
          fontSize: 10,
          letterSpacing: '.1em',
          color: 'var(--text-muted)',
        }}
      >
        {props.label}
      </span>
      <span
        data-testid={props.testid}
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontFamily: 'var(--font-data)',
          fontSize: 11,
          letterSpacing: '.06em',
          color:
            props.tone === 'bad'
              ? 'var(--red-text)'
              : props.tone === 'warn'
                ? 'var(--warn)'
                : 'var(--text-2)',
        }}
      >
        {props.value}
      </span>
    </div>
  );
}
